import React, {useEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {graphql} from 'react-relay';
import {Button, EMPHASIS, Icon, Label, NumberField, SegmentedControl} from '@forecast-it/design-system';
import {CommitMutation} from '../../../mutations/CommitMutation';
import {useForecastFetchQuery} from '../../../forecast-app/shared/hooks/useForecastFetchQuery';
import CreateBaselineVersionMutation from './CreateBaselineVersionMutation';
import {CreateBaselineVersionMutation as CreateBaselineVersionMutationType} from './__generated__/CreateBaselineVersionMutation.graphql';
import {CreateVersionPanelPreviewQuery} from './__generated__/CreateVersionPanelPreviewQuery.graphql';
import {CreateVersionPanelScalePreviewQuery} from './__generated__/CreateVersionPanelScalePreviewQuery.graphql';
import {TemplateChoice, TemplateOption} from './TemplateChoice';
import {C, F} from './versionsStyles';
import {
	copyPreview,
	DEFAULT_SCALE_PERCENT,
	formatHours,
	formatMoney,
	formatPercent,
	isScaleInRange,
	ScaleDirection,
	scaleRange,
	signedPercent,
	VersionPreview,
	VersionSource,
	VersionSummary,
} from './baselineVersionsMath';

export interface CreateVersionPanelProps {
	projectId: string;
	standard: VersionSummary;
	templates: ReadonlyArray<TemplateOption>;
	currencySymbol: string;
	onCreated: (versionId: string) => void;
	onCancel: () => void;
}

const PREVIEW_QUERY = graphql`
	query CreateVersionPanelPreviewQuery($projectId: ID, $templateId: ID!) {
		viewer {
			component(name: "baseline_version_preview")
			project(internalId: $projectId) {
				baselineTemplateVersionPreview(templateId: $templateId) {
					revenue
				}
			}
		}
	}
`;

// The server scales each time estimate on its own and prices the result exactly as creating the version does, so the
// preview matches the version that Create makes.
const SCALE_PREVIEW_QUERY = graphql`
	query CreateVersionPanelScalePreviewQuery(
		$projectId: ID
		$scaleDirection: BaselineVersionScaleDirection!
		$scalePercent: Int!
	) {
		viewer {
			component(name: "baseline_version_preview")
			project(internalId: $projectId) {
				baselineScaledVersionPreview(scaleDirection: $scaleDirection, scalePercent: $scalePercent) {
					revenue
					minutes
					margin
				}
			}
		}
	}
`;

const scaleKey = (direction: ScaleDirection, percent: number) => `${direction}:${percent}`;

/** The percent as typed so far, or null when the field is empty or holds something other than a number. */
const parseTypedPercent = (text: string): number | null => {
	const trimmed = text.trim();
	const value = trimmed === '' ? NaN : Number(trimmed);
	return Number.isNaN(value) ? null : value;
};

const Panel = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
	box-sizing: border-box;
	width: 420px;
	max-width: 100%;
	padding: 12px 14px;
	border: 2px solid ${C.brand};
	border-radius: 6px;
	background: ${C.brandTint};
`;

const Header = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
`;

const Title = styled.span`
	${F.sub1}
	color: ${C.text};
`;

const Subtitle = styled.span`
	${F.body2}
	color: ${C.aux};
`;

const Rows = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const Card = styled.div<{$selected: boolean; $disabled: boolean}>`
	display: flex;
	flex-direction: column;
	gap: 8px;
	padding: 10px 12px;
	background: ${C.surface};
	border: 1px solid ${p => (p.$selected ? C.brand : C.border)};
	box-shadow: ${p => (p.$selected ? `inset 0 0 0 1px ${C.brand}` : 'none')};
	border-radius: 6px;
	opacity: ${p => (p.$disabled ? 0.6 : 1)};
`;

const StartButton = styled.button`
	display: flex;
	align-items: flex-start;
	gap: 10px;
	padding: 0;
	border: none;
	background: transparent;
	font: inherit;
	text-align: left;
	color: ${C.text};
	cursor: pointer;
	&[aria-disabled='true'] {
		cursor: not-allowed;
	}
	&:focus-visible {
		outline: 2px solid ${C.brand};
		outline-offset: 2px;
	}
`;

const IconBox = styled.span`
	display: flex;
	flex: none;
	margin-top: 2px;
`;

const StartText = styled.span`
	display: flex;
	flex-direction: column;
	gap: 1px;
	min-width: 0;
`;

const StartTitle = styled.span`
	${F.sub2}
`;

const StartDesc = styled.span`
	${F.body2}
	color: ${C.aux};
`;

const Controls = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	padding-top: 4px;
`;

const ControlGroup = styled.div<{$width?: number}>`
	display: flex;
	flex-direction: column;
	gap: 6px;
	width: ${p => (p.$width ? `${p.$width}px` : 'auto')};
`;

const PreviewBox = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	padding: 8px 10px;
	background: ${C.surface};
	border: 1px solid ${C.border};
	border-radius: 6px;
`;

const PreviewText = styled.span`
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 0;
`;

const PreviewMain = styled.span`
	${F.sub1}
	color: ${C.text};
`;

const PreviewDetail = styled.span`
	${F.detail}
	color: ${C.aux};
`;

const Signed = styled.span`
	${F.detail}
	flex: none;
	color: ${C.brand};
`;

const Actions = styled.div`
	display: flex;
	gap: 8px;
`;

let nextPanelId = 0;

const START_ICONS: Record<VersionSource, string> = {COPY: 'phase', COPY_SCALED: 'clock', TEMPLATE: 'list'};

export const CreateVersionPanel = ({
	projectId,
	standard,
	templates,
	currencySymbol,
	onCreated,
	onCancel,
}: CreateVersionPanelProps) => {
	const {formatMessage} = useIntl();
	const [source, setSource] = useState<VersionSource>('COPY');
	const [direction, setDirection] = useState<ScaleDirection>('FEWER');
	const [percent, setPercent] = useState<number | null>(DEFAULT_SCALE_PERCENT);
	const [templateId, setTemplateId] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const inFlight = useRef(false);
	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);
	const panelId = useMemo(() => `create-version-${++nextPanelId}`, []);
	const titleId = `${panelId}-title`;
	// Revenue per template, filled from each fetch's own response so a slow earlier request can't
	// overwrite a later pick. null marks a failed fetch.
	const [revenues, setRevenues] = useState<Record<string, number | null>>({});
	const {fetch} = useForecastFetchQuery<
		CreateVersionPanelPreviewQuery['response'],
		CreateVersionPanelPreviewQuery['variables']
	>(PREVIEW_QUERY);
	// Scaled previews keyed by direction and percent, filled from each fetch's own response so a slow earlier
	// request can't overwrite a later choice. null marks a failed fetch.
	const [scaledPreviews, setScaledPreviews] = useState<Record<string, VersionPreview | null>>({});
	const scalePending = useRef(new Set<string>());
	const {fetch: fetchScaled} = useForecastFetchQuery<
		CreateVersionPanelScalePreviewQuery['response'],
		CreateVersionPanelScalePreviewQuery['variables']
	>(SCALE_PREVIEW_QUERY);

	const template = templates.find(t => t.id === templateId) ?? null;
	const scaleValid = isScaleInRange(direction, percent);
	const canCreate =
		!saving &&
		(source === 'COPY' || (source === 'COPY_SCALED' && scaleValid) || (source === 'TEMPLATE' && template != null));

	const pickTemplate = (id: string) => {
		setTemplateId(id);
		if (revenues[id] != null) {
			return;
		}
		if (id in revenues) {
			// A failed fetch is tried again; clear the dash so the ellipsis shows meanwhile.
			setRevenues(current => {
				const next = {...current};
				delete next[id];
				return next;
			});
		}
		fetch(
			{projectId, templateId: id},
			response => {
				if (!mounted.current) {
					return;
				}
				const revenue = response.viewer?.project?.baselineTemplateVersionPreview?.revenue;
				setRevenues(current => ({...current, [id]: revenue ?? null}));
			},
			() => {
				if (mounted.current) {
					setRevenues(current => ({...current, [id]: null}));
				}
			}
		);
	};

	const requestScaled = (nextDirection: ScaleDirection, nextPercent: number | null) => {
		if (nextPercent == null || !isScaleInRange(nextDirection, nextPercent)) {
			return;
		}
		const key = scaleKey(nextDirection, nextPercent);
		if (scaledPreviews[key] != null || scalePending.current.has(key)) {
			return;
		}
		scalePending.current.add(key);
		if (key in scaledPreviews) {
			// A failed fetch is tried again; clear the dash so the ellipsis shows meanwhile.
			setScaledPreviews(current => {
				const next = {...current};
				delete next[key];
				return next;
			});
		}
		const settle = (preview: VersionPreview | null) => {
			scalePending.current.delete(key);
			if (mounted.current) {
				setScaledPreviews(current => ({...current, [key]: preview}));
			}
		};
		fetchScaled(
			{projectId, scaleDirection: nextDirection, scalePercent: nextPercent},
			response => {
				const preview = response.viewer?.project?.baselineScaledVersionPreview;
				settle(
					preview
						? {revenue: preview.revenue ?? 0, minutes: preview.minutes ?? 0, margin: preview.margin ?? null}
						: null
				);
			},
			() => settle(null)
		);
	};

	const finish = () => {
		inFlight.current = false;
		setSaving(false);
	};

	const create = () => {
		if (!canCreate || inFlight.current) {
			return;
		}
		inFlight.current = true;
		setSaving(true);
		const input =
			source === 'COPY'
				? {projectId, source}
				: source === 'COPY_SCALED'
				? {projectId, source, scaleDirection: direction, scalePercent: percent}
				: {projectId, source, templateId};
		CommitMutation(
			CreateBaselineVersionMutation,
			input,
			(response: CreateBaselineVersionMutationType['response']) => {
				finish();
				const id = response.createBaselineVersion?.baselineVersion?.id;
				if (id) {
					onCreated(id);
				}
			},
			false,
			null,
			(error: Error, onFailure?: (e: Error) => void) => {
				finish();
				onFailure?.(error);
			}
		);
	};

	const startOptions: {id: VersionSource; title: string; desc: string; disabled: boolean}[] = [
		{
			id: 'COPY',
			title: formatMessage({id: 'baseline.versions.start_copy'}),
			desc: formatMessage({id: 'baseline.versions.start_copy_desc'}),
			disabled: false,
		},
		{
			id: 'COPY_SCALED',
			title: formatMessage({id: 'baseline.versions.start_scale'}),
			desc: formatMessage({id: 'baseline.versions.start_scale_desc'}),
			disabled: false,
		},
		{
			id: 'TEMPLATE',
			title: formatMessage({id: 'baseline.versions.start_template'}),
			desc: formatMessage({id: 'baseline.versions.start_template_desc'}),
			disabled: templates.length === 0,
		},
	];

	const money = (amount: number) => formatMoney(amount, currencySymbol);

	const renderPreview = () => {
		if (source === 'TEMPLATE') {
			if (!template) {
				return <PreviewDetail>{formatMessage({id: 'baseline.versions.pick_template'})}</PreviewDetail>;
			}
			const arrived = template.id in revenues;
			const revenue = revenues[template.id];
			return (
				<PreviewText>
					<PreviewMain>{!arrived ? '…' : revenue == null ? '—' : money(revenue)}</PreviewMain>
					<PreviewDetail>
						{formatMessage(
							{id: 'baseline.versions.preview_template'},
							{hours: formatHours(template.totalMinutes ?? 0), count: template.phaseCount ?? 0}
						)}
					</PreviewDetail>
				</PreviewText>
			);
		}
		if (source === 'COPY_SCALED') {
			if (!scaleValid || percent == null) {
				const {min, max} = scaleRange(direction);
				return <PreviewDetail>{formatMessage({id: 'baseline.versions.scale_range'}, {min, max})}</PreviewDetail>;
			}
			const key = scaleKey(direction, percent);
			const arrived = key in scaledPreviews;
			const scaled = scaledPreviews[key];
			const hours = scaled ? formatHours(scaled.minutes) : '';
			return (
				<>
					<PreviewText>
						<PreviewMain>{!arrived ? '…' : scaled == null ? '—' : money(scaled.revenue)}</PreviewMain>
						{scaled ? (
							<PreviewDetail>
								{scaled.margin == null
									? formatMessage({id: 'baseline.versions.preview_scaled_no_margin'}, {hours})
									: formatMessage(
											{id: 'baseline.versions.preview_scaled'},
											{hours, margin: formatPercent(scaled.margin)}
									  )}
							</PreviewDetail>
						) : null}
					</PreviewText>
					<Signed>{signedPercent(direction, percent)}</Signed>
				</>
			);
		}
		const copy = copyPreview(standard);
		return (
			<PreviewText>
				<PreviewMain>{money(copy.revenue)}</PreviewMain>
				<PreviewDetail>
					{formatMessage({id: 'baseline.versions.preview_copy'}, {hours: formatHours(copy.minutes)})}
				</PreviewDetail>
			</PreviewText>
		);
	};

	const directionLabel = formatMessage({id: 'baseline.versions.adjust_hours'});

	return (
		<Panel role="dialog" aria-label={formatMessage({id: 'baseline.versions.panel_title'})}>
			<Header>
				<Title id={titleId}>{formatMessage({id: 'baseline.versions.panel_title'})}</Title>
				<Subtitle>{formatMessage({id: 'baseline.versions.panel_subtitle'})}</Subtitle>
			</Header>

			<Rows role="group" aria-labelledby={titleId}>
				{startOptions.map(option => {
					const selected = source === option.id;
					return (
						<Card
							key={option.id}
							$selected={selected}
							$disabled={option.disabled}
							onClick={() => {
								if (!option.disabled) {
									setSource(option.id);
									// Clicks on the scale controls bubble up here too; those request their own preview.
									if (option.id === 'COPY_SCALED' && source !== 'COPY_SCALED') {
										requestScaled(direction, percent);
									}
								}
							}}
						>
							<StartButton type="button" role="radio" aria-checked={selected} aria-disabled={option.disabled}>
								<IconBox>
									<Icon icon={START_ICONS[option.id]} size="s" color={selected ? 'primary' : 'default'} />
								</IconBox>
								<StartText>
									<StartTitle>{option.title}</StartTitle>
									<StartDesc>
										{option.id === 'TEMPLATE' && option.disabled
											? formatMessage({id: 'baseline.versions.no_templates'})
											: option.desc}
									</StartDesc>
								</StartText>
							</StartButton>
							{selected && option.id === 'COPY_SCALED' ? (
								<Controls>
									<ControlGroup>
										<Label>{directionLabel}</Label>
										<SegmentedControl
											label={directionLabel}
											value={direction}
											onValueChange={(next: unknown) => {
												if (next === 'FEWER' || next === 'MORE') {
													setDirection(next);
													requestScaled(next, percent);
												}
											}}
										>
											<SegmentedControl.Item
												value="FEWER"
												label={formatMessage({id: 'baseline.versions.fewer_hours'})}
											>
												{formatMessage({id: 'baseline.versions.fewer_hours'})}
											</SegmentedControl.Item>
											<SegmentedControl.Item
												value="MORE"
												label={formatMessage({id: 'baseline.versions.more_hours'})}
											>
												{formatMessage({id: 'baseline.versions.more_hours'})}
											</SegmentedControl.Item>
										</SegmentedControl>
									</ControlGroup>
									<ControlGroup
										$width={120}
										// The field reports its value only on blur or Enter; reading the typed text here
										// keeps the preview and the range check in step with every keystroke.
										onChange={(event: React.FormEvent<HTMLDivElement>) => {
											if (event.target instanceof HTMLInputElement) {
												const typed = parseTypedPercent(event.target.value);
												setPercent(typed);
												requestScaled(direction, typed);
											}
										}}
									>
										<Label>{formatMessage({id: 'baseline.versions.by'})}</Label>
										<NumberField
											aria-label={formatMessage({id: 'baseline.versions.by'})}
											// Left uncontrolled: feeding the value back on every keystroke would reformat
											// a half-typed number. It remounts with the current percent.
											defaultValue={percent ?? undefined}
											hasError={!scaleValid}
											suffixText="%"
											allowEmpty
											width={NumberField.WIDTH.FLEX}
											align={NumberField.ALIGN.RIGHT}
											onChange={(next: number) => {
												const value = Number.isNaN(next) ? null : next;
												setPercent(value);
												requestScaled(direction, value);
											}}
										/>
									</ControlGroup>
								</Controls>
							) : null}
							{selected && option.id === 'TEMPLATE' ? (
								<TemplateChoice templates={templates} value={templateId} onChange={pickTemplate} />
							) : null}
						</Card>
					);
				})}
			</Rows>

			<PreviewBox>{renderPreview()}</PreviewBox>

			<Actions>
				<Button emphasis={EMPHASIS.HIGH} disabled={!canCreate} onPress={create}>
					{formatMessage({id: 'baseline.versions.create_and_open'})}
				</Button>
				<Button emphasis={EMPHASIS.LOW} onPress={onCancel}>
					{formatMessage({id: 'common.cancel'})}
				</Button>
			</Actions>
		</Panel>
	);
};
